import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsService {
  protected readonly platformId = inject(PLATFORM_ID);
  private readonly scriptId = 'informally-ga-script';
  private isScriptLoading = false;
  private isInitialised = false;

  protected trackingId: string;

  init(trackingId: string) {
    const normalizedTrackingId = String(trackingId || '').trim();
    if (!normalizedTrackingId || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.trackingId = normalizedTrackingId;
    this.ensureDataLayer();

    if (!this.isInitialised) {
      this.gtag('js', new Date());
      // In SPA mode we emit route page views manually from router navigation events.
      this.gtag('config', this.trackingId, { send_page_view: false });
      this.isInitialised = true;
    }

    if (document.getElementById(this.scriptId) || this.isScriptLoading) {
      return;
    }

    const script = document.createElement('script');
    script.id = this.scriptId;
    script.type = 'text/javascript';
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.trackingId}`;
    script.async = true;
    script.onload = () => {
      this.isScriptLoading = false;
    };
    script.onerror = () => {
      this.isScriptLoading = false;
    };

    this.isScriptLoading = true;
    document.head.appendChild(script);
  }

  gtag(...args: any[]) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Match the canonical gtag snippet payload shape.
    this.ensureDataLayer().push(arguments);
  }

  pageView(options?) {
    this.gtag('config', this.trackingId, options);
  }

  trackPageView(path: string) {
    if (!this.trackingId) {
      return;
    }

    this.gtag('event', 'page_view', {
      page_path: path,
    });
  }

  private ensureDataLayer(): any[] {
    const windowRef = window as unknown as { dataLayer?: any[] };
    windowRef.dataLayer = windowRef.dataLayer || [];
    return windowRef.dataLayer;
  }
}
